import type { DrawingTool } from "../drawingTools.ts";
import { useT } from "../../i18n/hooks.ts";
import type { GisColumnMapping, SubBasinColumnMapping } from "../../model/projectEntities.ts";
import type { SeriesSelection } from "../mapModes.ts";
import type { MapMitecoMode } from "../../model/mitecoLayers.ts";
import { Step2Zones } from "../Step2Zones.tsx";
import { useProjectEditing } from "./hooks.ts";
import { AquifersPanel } from "./AquifersPanel.tsx";
import { GenericLayersPanel } from "./GenericLayersPanel.tsx";
import { OutletPointsPanel } from "./OutletPointsPanel.tsx";
import { PANELS, type PanelKind } from "./panelDefinitions.ts";
import { SeriesPanel } from "./SeriesPanel.tsx";
import { SubBasinsPanel } from "./SubBasinsPanel.tsx";

/**
 * Replaces EditorProyecto.tsx: instead of an always-open panel with the
 * wizard's same steps in tabs, this is direct access to the 5 catalogs
 * (sub-basins, zones, outlet points, aquifers, input series), with tabs
 * always visible right below the header. Every edit opens its own dialog
 * (Dialog.tsx) instead of replacing the element in place.
 *
 * "Link GIS layer" and "Draw on the map" are NOT their own tabs — they
 * used to be ("GIS layer"/"Draw"), but that forced jumping between tabs
 * to, e.g., draw a sub-basin and then go to "Sub-basins" to see it in the
 * list. Now each catalog (Sub-basins/SubBasinsPanel.tsx, Outlet points/
 * OutletPointsPanel.tsx, Aquifers/AquifersPanel.tsx) has its own draw
 * button and its own (collapsible, closed as soon as something is linked)
 * link-GIS-layer section — each with the layer it corresponds to
 * (`gisLayer` for sub-basins, `outletPointsLayer`/`aquifersLayer` for the
 * other two, totally independent from each other). "Zones" and "Input
 * series" have no geometry of their own, so they carry neither section.
 *
 * Multi-selection on the map (click/Shift+drag) only makes sense for
 * sub-basins — its notice and bulk actions (assign zone, delete several...)
 * live inside SubBasinsPanel.tsx, below that tab's title, not here: they
 * used to always be visible above, even above the tab bar itself,
 * regardless of which catalog was being viewed.
 */
export function EditBar({
  selection,
  onChangeSelection,
  seriesSelection,
  onChangeSeriesSelection,
  mitecoMode,
  onToggleMitecoMode,
  onChangeMitecoLayer,
  onAddSelectedMiteco,
  onRecoverFromGis,
  onCreateOutletPointsFromGis,
  onCreateAquifersFromGis,
  onClose,
  onOpenBulkAemet,
  drawingMode,
  onStartDrawing,
  onCancelDrawing,
  panel,
  onChangePanel,
  zoneAssignMode,
  onChangeZoneAssignMode,
}: {
  selection: Set<number>;
  onChangeSelection: (selection: Set<number>) => void;
  /** Sub-basin/aquifer to filter the "Input series" charts by — see SeriesPanel.tsx/SubBasinMap.tsx's MapSeriesMode. */
  seriesSelection: SeriesSelection | null;
  onChangeSeriesSelection: (selection: SeriesSelection) => void;
  /** SubBasinsPanel.tsx's own "Add from MITECO" mode — only meaningful there, unlike selection/seriesSelection above which several panels share. */
  mitecoMode: MapMitecoMode | undefined;
  onToggleMitecoMode: () => void;
  onChangeMitecoLayer: (layer: "basins" | "subBasins") => void;
  onAddSelectedMiteco: () => void;
  /** Recovers deleted sub-basins by reimporting the original GIS — see GisLayerPanel.tsx. */
  onRecoverFromGis: (geojson: GeoJSON.FeatureCollection, mapping: SubBasinColumnMapping) => void;
  /** Creates new outlet points/aquifers from the geometries of an uploaded file that don't match any existing one — see LinkGisPanel.tsx. */
  onCreateOutletPointsFromGis: (
    geojson: GeoJSON.FeatureCollection,
    mapping: GisColumnMapping
  ) => void;
  onCreateAquifersFromGis: (geojson: GeoJSON.FeatureCollection, mapping: GisColumnMapping) => void;
  onClose: () => void;
  /** Closes this whole panel and opens AemetBulkFetchDialog.tsx — see SeriesPanel.tsx's own "Get from AEMET" button. An optional sub-basin code list pre-scopes the dialog instead of the usual "whole project". */
  onOpenBulkAemet: (onlyCodes?: number[]) => void;
  /** Active drawing tool on the map — see DrawButton.tsx/SubBasinMap.tsx. */
  drawingMode: DrawingTool | null;
  onStartDrawing: (tool: DrawingTool) => void;
  onCancelDrawing: () => void;
  /** Which catalog to show below — chosen from the pencil menu (TopBar.tsx) on open; can keep being changed from the tab bar here without going back to that menu. Lives in App.tsx, not here, precisely so the pencil menu can set it directly. */
  panel: PanelKind;
  onChangePanel: (panel: PanelKind) => void;
  /** Which assignment table Step2Zones.tsx (the "zones" panel) is showing — see its own doc comment for why this is controlled from App.tsx. */
  zoneAssignMode: "zone" | "snow";
  onChangeZoneAssignMode: (mode: "zone" | "snow") => void;
}) {
  const t = useT();
  // The stable project-data slice (project/gisLayer/series/...) — read from
  // context instead of as props (see ProjectEditingProvider, context.tsx).
  // Still forwarded explicitly to Step2Zones below, which is also used by
  // the new-project wizard (no Provider there) and so keeps plain props.
  const { project, gisLayer, onChangeProject, genericLayers, onChangeGenericLayers } =
    useProjectEditing();
  return (
    <aside className="edit-panel">
      <div className="edit-panel-header">
        <h2>{t("topbar.editProject")}</h2>
        <button className="button-icon" onClick={onClose} aria-label={t("editBar.closeEdit")}>
          ✕
        </button>
      </div>

      <div className="edit-panel-tabs">
        {PANELS.map((p) => (
          <button
            key={p.id}
            className={p.id === panel ? "tab-active" : undefined}
            onClick={() => onChangePanel(panel === p.id ? null : p.id)}
          >
            <p.Icon /> {t(p.label)}
          </button>
        ))}
      </div>

      {panel && (
        <div className="edit-panel-body">
          {panel === "subBasins" && (
            <SubBasinsPanel
              onRecoverFromGis={onRecoverFromGis}
              selection={selection}
              onChangeSelection={onChangeSelection}
              mitecoMode={mitecoMode}
              onToggleMitecoMode={onToggleMitecoMode}
              onChangeMitecoLayer={onChangeMitecoLayer}
              onAddSelectedMiteco={onAddSelectedMiteco}
              drawingMode={drawingMode}
              onStartDrawing={onStartDrawing}
              onCancelDrawing={onCancelDrawing}
            />
          )}
          {panel === "zones" && (
            <Step2Zones
              project={project}
              gisLayer={gisLayer}
              onChange={onChangeProject}
              selection={selection}
              onChangeSelection={onChangeSelection}
              assignMode={zoneAssignMode}
              onChangeAssignMode={onChangeZoneAssignMode}
            />
          )}
          {panel === "outlets" && (
            <OutletPointsPanel
              onCreateOutletPointsFromGis={onCreateOutletPointsFromGis}
              drawingMode={drawingMode}
              onStartDrawing={onStartDrawing}
              onCancelDrawing={onCancelDrawing}
              selection={selection}
              onChangeSelection={onChangeSelection}
            />
          )}
          {panel === "aquifers" && (
            <AquifersPanel
              onCreateAquifersFromGis={onCreateAquifersFromGis}
              drawingMode={drawingMode}
              onStartDrawing={onStartDrawing}
              onCancelDrawing={onCancelDrawing}
              selection={selection}
              onChangeSelection={onChangeSelection}
            />
          )}
          {panel === "series" && (
            <SeriesPanel
              seriesSelection={seriesSelection}
              onChangeSeriesSelection={onChangeSeriesSelection}
              onOpenBulkAemet={onOpenBulkAemet}
            />
          )}
          {panel === "layers" && (
            <GenericLayersPanel layers={genericLayers} onChange={onChangeGenericLayers} />
          )}
        </div>
      )}
    </aside>
  );
}
